import React, { useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { fileToResizedDataUrl } from '@/lib/imageResize';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { X, Plus, Trash2, Loader2, Image as ImageIcon, Save } from 'lucide-react';
import { tagThemeClass } from './tagTheme';

const MATERIAL_TYPES = [
  { v: 'diamond', l: 'Diamond' },
  { v: 'color_stone', l: 'Colour Stone' },
  { v: 'pearl', l: 'Pearl' },
  { v: 'other', l: 'Other' },
];

const INPUT = 'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-1 focus-visible:ring-offset-background';
const LABEL = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * TagManualTagDialog — create or edit a manual tag (tag_only). Uses the same
 * core fields as a portal jewellery tag. In-scope inline modal so the neutral
 * `.tag-scope` tokens apply (radix portals would escape the scope).
 */
export function TagManualTagDialog({ open, mode = 'create', initial = null, masters, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [image, setImage] = useState('');
  const [jewNo, setJewNo] = useState('');
  const [title, setTitle] = useState('');
  const [jewType, setJewType] = useState('');
  const [purityName, setPurityName] = useState('');
  const [gross, setGross] = useState('');
  const [net, setNet] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const purities = masters?.gold_purities || [];
  const types = masters?.jewellery_types || [];

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setImage(initial.image || '');
      setJewNo(initial.jewellery_no || '');
      setTitle(initial.title || '');
      setJewType(initial.jewellery_type || '');
      setPurityName(initial.gold_purity_name || '');
      setGross(initial.gross_weight_grams != null ? String(initial.gross_weight_grams) : '');
      setNet(initial.net_weight_grams != null ? String(initial.net_weight_grams) : '');
      const snap = Array.isArray(initial.rm_consumed_snapshot) ? initial.rm_consumed_snapshot : [];
      setRows(snap.map((r) => ({
        classification: r.classification_schema || r.classification || '',
        carats: r.carats != null ? String(r.carats) : '',
        pieces: r.pieces != null ? String(r.pieces) : '',
        material_type: r.material_type || 'other',
      })));
    } else {
      setImage(''); setJewNo(''); setTitle(''); setJewType(''); setPurityName(''); setGross(''); setNet('');
      setRows([{ classification: '', carats: '', pieces: '', material_type: 'diamond' }]);
    }
  }, [open, mode, initial]);

  const onPickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const url = await fileToResizedDataUrl(file); setImage(url); }
    catch (e) { toast.error('Could not load that image.'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const addRow = () => setRows((r) => [...r, { classification: '', carats: '', pieces: '', material_type: 'diamond' }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const setRow = (i, k, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: val } : row)));

  const canSave = jewNo.trim().length > 0 && !busy;

  const submit = async () => {
    if (!jewNo.trim()) { toast.error('Jewellery number is required.'); return; }
    setBusy(true);
    const p = purities.find((x) => x.name === purityName);
    const materials = rows
      .filter((r) => (r.classification || '').trim())
      .map((r) => ({
        classification: r.classification.trim(),
        carats: parseFloat(r.carats || '0') || 0,
        pieces: parseInt(r.pieces || '0', 10) || 0,
        material_type: r.material_type || 'other',
      }));
    const payload = {
      jewellery_no: jewNo.trim(),
      title: title.trim(),
      jewellery_type: jewType.trim(),
      gold_purity_name: purityName || null,
      gold_purity_percent: p ? p.percent : null,
      gross_weight_grams: parseFloat(gross || '0') || 0,
      net_weight_grams: parseFloat(net || '0') || 0,
      image: image || '',
      materials,
    };
    try {
      if (mode === 'edit' && initial) {
        await notesApi.patch(`/notes/tag-workspace/manual/${initial.id}`, payload);
        toast.success('Tag updated.');
      } else {
        await notesApi.post('/notes/tag-workspace/manual', payload);
        toast.success('Manual tag created.');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(extractError(e, 'Could not save the tag.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[200] flex items-center justify-center p-4`} data-testid="tagws-manual-tag-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={() => !busy && onClose?.()} />
      <div className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">{mode === 'edit' ? 'Edit manual tag' : 'Create manual tag'}</h3>
            <p className="text-xs text-muted-foreground">Enter the same core details as a jewellery tag.</p>
          </div>
          <button onClick={() => !busy && onClose?.()} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
          <div className="space-y-3">
            <div>
              <span className={LABEL}>Image</span>
              <div className="flex items-center gap-3">
                <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-secondary">
                  {image ? <img src={image} alt="preview" className="h-full w-full object-contain p-1" /> : <ImageIcon size={22} className="text-muted-foreground" />}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-secondary">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : null} {image ? 'Replace' : 'Upload'} image
                  </button>
                  {image && <button type="button" onClick={() => setImage('')} className="text-left text-xs text-muted-foreground hover:text-destructive">Remove</button>}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="tagws-manual-tag-image-input" onChange={(e) => onPickImage(e.target.files?.[0])} />
                </div>
              </div>
            </div>
            <div>
              <span className={LABEL}>Jewellery number *</span>
              <input value={jewNo} onChange={(e) => setJewNo(e.target.value)} placeholder="e.g. RNG-1001" className={`${INPUT} font-mono`} data-testid="tagws-manual-tag-number-input" />
            </div>
            <div>
              <span className={LABEL}>Title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short description" className={INPUT} data-testid="tagws-manual-tag-title-input" />
            </div>
            <div>
              <span className={LABEL}>Type of jewellery</span>
              <select value={jewType} onChange={(e) => setJewType(e.target.value)} className={INPUT} data-testid="tagws-manual-tag-type-select">
                <option value="">Select type…</option>
                {types.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <span className={LABEL}>Gold purity</span>
              <select value={purityName} onChange={(e) => setPurityName(e.target.value)} className={INPUT} data-testid="tagws-manual-tag-purity-select">
                <option value="">Select purity…</option>
                {purities.map((p) => <option key={p.id} value={p.name}>{p.name}{p.percent ? ` (${p.percent}%)` : ''}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className={LABEL}>Gross weight (g)</span>
                <input type="number" step="0.001" min="0" value={gross} onChange={(e) => setGross(e.target.value)} className={`${INPUT} font-mono`} data-testid="tagws-manual-tag-gross-weight-input" />
              </div>
              <div>
                <span className={LABEL}>Net weight (g)</span>
                <input type="number" step="0.001" min="0" value={net} onChange={(e) => setNet(e.target.value)} className={`${INPUT} font-mono`} data-testid="tagws-manual-tag-net-weight-input" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`${LABEL} mb-0`}>Raw materials</span>
              <button type="button" onClick={addRow} data-testid="tagws-raw-material-add-row-button" className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-xs text-foreground hover:bg-secondary"><Plus size={13} /> Add row</button>
            </div>
            <div className="space-y-2">
              {rows.length === 0 && <div className="rounded-lg border border-dashed border-border p-3 text-center text-xs text-muted-foreground">No materials. Add rows for diamonds, colour stones or pearls.</div>}
              {rows.map((row, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-border bg-secondary/40 p-2.5" data-testid="tagws-raw-material-row">
                  <div className="flex items-center gap-2">
                    <input value={row.classification} onChange={(e) => setRow(i, 'classification', e.target.value)} placeholder="Classification (e.g. VVS Round)" className={`${INPUT} py-1.5`} />
                    <button type="button" onClick={() => removeRow(i)} aria-label="Remove row" data-testid="tagws-raw-material-remove-row-button" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-background hover:text-destructive"><Trash2 size={14} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <select value={row.material_type} onChange={(e) => setRow(i, 'material_type', e.target.value)} className={`${INPUT} py-1.5`}>
                      {MATERIAL_TYPES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <input type="number" step="0.001" min="0" value={row.carats} onChange={(e) => setRow(i, 'carats', e.target.value)} placeholder="Carats" className={`${INPUT} py-1.5 font-mono`} />
                    <input type="number" step="1" min="0" value={row.pieces} onChange={(e) => setRow(i, 'pieces', e.target.value)} placeholder="Pcs" className={`${INPUT} py-1.5 font-mono`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-4">
          <button onClick={() => !busy && onClose?.()} data-testid="tagws-manual-tag-cancel-button" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm text-foreground hover:bg-secondary">Cancel</button>
          <button onClick={submit} disabled={!canSave} data-testid="tagws-manual-tag-save-button" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:opacity-90 disabled:opacity-50 active:scale-[0.98]">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {mode === 'edit' ? 'Save changes' : 'Create tag'}
          </button>
        </div>
      </div>
    </div>
  );
}
